import { useState, useMemo } from 'react';
import { useStore } from '../../store/useStore';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import { useGLTF, Text } from '@react-three/drei';
import * as THREE from 'three';

interface HouseProps {
    position: [number, number, number];
    contentId: string;
    label: string;
    color?: string;
}

export const House = ({ position, contentId, label, color = '#cc5555' }: HouseProps) => {
    const [hover, setHover] = useState(false);
    const setCurrentScene = useStore((s) => s.setCurrentScene);
    const updateQuestProgress = useStore((s) => s.updateQuestProgress);
    const addStat = useStore((s) => s.addStat);
    const house01 = useGLTF('/assets/Blender/GLB/House01.glb');
    const house02 = useGLTF('/assets/Blender/GLB/House02.glb');

    const prepared = useMemo(() => {
        const hash = [...contentId].reduce((acc, c) => acc + c.charCodeAt(0), 0);
        const index = hash % 2;
        const base = index === 0 ? house01.scene : house02.scene;
        // Adjusted scale based on user feedback (was too large)
        const scale = index === 0 ? 1.5 : 1.0; 

        const clone = base.clone(true);
        clone.scale.setScalar(scale);
        
        // Manual bounding box calculation
        const box = new THREE.Box3();
        clone.updateWorldMatrix(true, true);
        let hasGeometry = false;

        clone.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (mesh.isMesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
                
                // Ensure material is visible and valid
                if (mesh.material) {
                    // Clone material to avoid shared state issues
                    const mat = Array.isArray(mesh.material) 
                        ? mesh.material.map(m => m.clone()) 
                        : mesh.material.clone();

                    // Apply standard fix for visibility
                    const applyFix = (m: THREE.Material) => {
                        m.side = THREE.DoubleSide; // Fix visibility from all angles
                        m.transparent = false;     // Force opaque
                        m.opacity = 1.0;
                        m.depthWrite = true;       // Fix depth sorting issues
                        m.depthTest = true;
                        
                        // Fix for potentially dark materials
                        if (m instanceof THREE.MeshStandardMaterial) {
                            m.roughness = 0.8;
                            m.metalness = 0.1; 
                            m.envMapIntensity = 0.5;
                        }
                        
                        m.needsUpdate = true;
                    };

                    if (Array.isArray(mat)) {
                        mat.forEach(m => applyFix(m));
                    } else {
                        applyFix(mat as THREE.Material);
                    }
                    
                    mesh.material = mat;
                }
            }
            
            if (mesh.isMesh && mesh.geometry) {
                mesh.geometry.computeBoundingBox();
                if (mesh.geometry.boundingBox) {
                    const bb = mesh.geometry.boundingBox.clone();
                    bb.applyMatrix4(mesh.matrixWorld);
                    box.union(bb);
                    hasGeometry = true;
                }
            }
        });

        if (hasGeometry && !box.isEmpty()) {
             // Center vertically around the bottom
             const yOffset = -box.min.y;
             clone.position.y += yOffset;
        } else {
             // Fallback if geometry detection fails: just shift up a bit
             clone.position.y += 2;
        }

        return { clone, hasGeometry };
    }, [contentId, house01.scene, house02.scene]);

    const enterHouse = () => {
        const houseId = `house_${contentId}`;
        setCurrentScene(houseId);
        updateQuestProgress('explore', houseId, 1);
        addStat('explore', 1, houseId);
    };

    // Use a primitive directly to ensure it renders, bypassing RigidBody for a test if needed
    // But we need collision. 
    return (
        <group position={position}>
            {/* Visual House */}
            <RigidBody
                type="fixed"
                colliders="cuboid"
                userData={{
                    type: 'interactable',
                    id: `house_${contentId}`,
                    onInteract: () => enterHouse()
                }}
            >
                {/* Visual Model */}
                <primitive object={prepared.clone} />
                
                {/* Fallback Box if model fails to load (Debug) */}
                <mesh position={[0, 5, 0]} visible={!prepared.hasGeometry}>
                    <boxGeometry args={[10, 10, 10]} />
                    <meshStandardMaterial color={color} wireframe />
                </mesh>
            </RigidBody>

            {/* Label (Troika 3D Text) */}
            <Text
                position={[0, 6, 0]}
                fontSize={1}
                color="white"
                outlineWidth={0.05}
                outlineColor="black"
                anchorX="center"
                anchorY="middle"
                fillOpacity={hover ? 1 : 0.6}
            >
                {label}
            </Text>

            {/* Trigger Zone (Sensor) */}
            <RigidBody
                type="fixed"
                colliders={false} // Custom sensor collider
                onIntersectionEnter={(e) => {
                    if (e.other.rigidBodyObject?.name === 'player') {
                        setHover(true);
                    }
                }}
                onIntersectionExit={(e) => {
                    if (e.other.rigidBodyObject?.name === 'player') {
                        setHover(false);
                    }
                }}
            >
                <CuboidCollider
                    args={[3, 3, 3]}
                    position={[0, 2.5, 0]}
                    sensor
                    onIntersectionEnter={() => setHover(true)}
                    onIntersectionExit={() => setHover(false)}
                />
            </RigidBody>

            {/* Interaction Prompt or Click Handler */}
            {hover && (
                // You can replace this with a 3D mesh or TroikaText if you want a 3D prompt
                // <TroikaText text="Click to Enter" position={[0, 3, 4]} fontSize={0.4} color="white" />
                null
            )}
        </group>
    );
};

useGLTF.preload('/assets/Blender/GLB/House01.glb');
useGLTF.preload('/assets/Blender/GLB/House02.glb');
